import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import {
  StyledCourseHero,
  Overlay,
  TitleBlock,
  Overtitle,
  Inline,
  ProgressBlock,
  ProgressInfo,
  ProgressNumber,
  BadgeCtaBlock,
  BadgeContainer,
  ButtonContainer,
  Shadow,
} from './style';
import { CTA, ProgressBar, Text, Icon, Badge, HtmlRaw } from '../../../atoms';
import theme from '../../../theme';
import { convertDuration } from '../../../../utils/common';
import { Container } from '../../../gridSystem';
import { withMediaQueries } from '../../../../app/hoc/withMediaQueries';
import { MODAL_OPEN } from '../../../../app/redux/actions';
import { ModalLeaveCourseId } from '../../../atoms/Modals/ModalLeaveCourse';
import { ModalSurveyId } from '../../../atoms/Modals/ModalSurvey';
import { getToken } from '../../../../app/api/token';
import { apiHost, baseUrl } from '../../../../app/api/config';
import { ToastError } from '../../Toast';
import { COURSE_RECOMMEND } from '../../../../app/redux/actions/coursedetails';
import { useHighlightText } from '../../../../utils/highlight';

const CourseHero = ({ data, mediaIsPhone, modalOpen, recommendCourse }) => {
  const downloadRef = useRef(null);
  const [fileError, setFileError] = useState(false);

  useEffect(() => {
    const token = getToken()?.token;
    if (
      token &&
      data?.id &&
      data?.completion_certificate &&
      data?.completion?.status === 'completed' &&
      downloadRef
    ) {
      fetch(`${apiHost}${baseUrl}pzzl_certificate/course/${data.id}`, {
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          if (!res?.ok) throw new Error();
          return res.blob();
        })
        .then((blob) => {
          const href = window.URL.createObjectURL(blob);
          const a = downloadRef.current;
          a.download = `certificato ${data?.title || data?.id}.pdf`;
          a.href = href;
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
          setFileError(true);
        });
    }
  }, [data, downloadRef]);

  useEffect(() => {
    if (fileError) {
      toast.error(
        <ToastError
          title="Qualcosa è andato storto!"
          body="Si prega di riprovare più tardi"
        />
      );
    }
  }, [fileError]);

  return (
    <StyledCourseHero cover={data?.cover}>
      <Shadow>
        <Container className="pt-0">
          <Overlay>
            <TitleBlock>
              <Badge type="white">{data?.code}</Badge>
              <Overtitle>
                {data?.categories?.map((item, index) => (
                  <span key={`item-${index}`}>{item.label}</span>
                ))}
              </Overtitle>
              <Text type="h1" className="course-title">
                <HtmlRaw html={useHighlightText(data?.title)} />
              </Text>
            </TitleBlock>
            <div>
              <ProgressBlock>
                {data?.completion?.status !== 'completed' && (
                  <ProgressInfo>
                    <Inline>
                      <Text type="b4">
                        <span>Stai seguendo: </span>
                        {data?.current_activity?.name}
                      </Text>
                      <Icon
                        type="marker-pin-06"
                        stroke="white"
                        size={24}
                        clickable={false}
                      />
                    </Inline>
                    <ProgressNumber>
                      {Number(((data?.completion?.rate || 0) * 100).toFixed(0))}
                      %
                    </ProgressNumber>
                  </ProgressInfo>
                )}
                {data?.completion?.completion_date ? (
                  <Text type="h2">
                    Corso completato il{' '}
                    {dayjs(data.completion.completion_date * 1000).format(
                      'DD/MM/YYYY'
                    )}
                    <Icon type="check-circle" />
                  </Text>
                ) : (
                  <ProgressBar
                    percentage={Number(
                      ((data?.completion?.rate || 0) * 100).toFixed(0)
                    )}
                    color="#0077CE"
                    bgColor={theme().colors.white}
                  />
                )}
              </ProgressBlock>
              <BadgeCtaBlock>
                <BadgeContainer>
                  <div style={{ marginRight: 'auto' }}>
                    <Inline>
                      <Text type="b4">{convertDuration(data?.duration)}</Text>
                      <Icon
                        type="clock"
                        stroke="white"
                        size={24}
                        clickable={false}
                      />
                    </Inline>
                    <Inline>
                      <Text type="b4">
                        {data?.total_activities}&nbsp;attività
                      </Text>
                      <Icon
                        type="puzzle-piece-01"
                        stroke="white"
                        size={24}
                        clickable={false}
                      />
                    </Inline>
                  </div>
                  {data?.course_status === 'expired' && (
                    <Badge type="expired" uppercase>
                      Scaduto
                    </Badge>
                  )}
                  {data?.created && (
                    <Badge type="outline" color={theme().colors.white}>
                      {dayjs(data.created * 1000).format('YYYY')}
                    </Badge>
                  )}
                </BadgeContainer>
                <ButtonContainer>
                  {data?.completion?.status === 'completed' ? (
                    <>
                      {data?.completion_certificate && (
                        <>
                          <Text type="b1">
                            È disponibile l’attestato di frequenza.
                          </Text>
                          <a
                            href="#"
                            ref={downloadRef}
                            className="download"
                            aria-label="Download"
                          >
                            <CTA
                              type="secondary"
                              btnText="Download"
                              fullsize={mediaIsPhone}
                            />
                          </a>
                        </>
                      )}
                      {data?.satisfaction_survey?.completion_status ===
                        'incomplete' && (
                        <CTA
                          type="secondary"
                          btnText="Questionario"
                          fullsize={mediaIsPhone}
                          onClick={() =>
                            modalOpen(ModalSurveyId, {
                              id: data?.id,
                              title: data?.title,
                            })
                          }
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {data?.typology === 'course' &&
                        Object.keys(data?.recommended_info)?.length > 0 && (
                          <CTA
                            type="secondary"
                            btnText="consiglia"
                            fullsize={mediaIsPhone}
                            onClick={() => recommendCourse(data?.id)}
                            icon="heart-rounded"
                            classes={`btn-recommend ${
                              data?.recommended_info?.recommended
                                ? 'recommended'
                                : ''
                            }`}
                          />
                        )}
                      {data?.typology === 'course' && (
                        <CTA
                          type="secondary"
                          btnText="abbandona"
                          fullsize={mediaIsPhone}
                          onClick={() =>
                            modalOpen(ModalLeaveCourseId, {
                              id: data?.id,
                              title: data?.title,
                              formDetailsPage: true,
                            })
                          }
                        />
                      )}
                    </>
                  )}
                </ButtonContainer>
              </BadgeCtaBlock>
            </div>
          </Overlay>
        </Container>
      </Shadow>
    </StyledCourseHero>
  );
};

CourseHero.propTypes = {
  data: PropTypes.object,
  mediaIsPhone: PropTypes.bool,
  modalOpen: PropTypes.func,
  recommendCourse: PropTypes.func,
};

export default connect(
  (state) => {
    const data = state.coursedetails;
    return {
      data,
    };
  },
  (dispatch) => ({
    modalOpen: (id, payload = {}) =>
      dispatch({ type: MODAL_OPEN, id, payload }),
    recommendCourse: (id) => dispatch({ type: COURSE_RECOMMEND._REQUEST, id }),
  })
)(withMediaQueries(CourseHero));
